import { Check, CircleAlert, ExternalLink, GitMerge, GitPullRequest, Tag } from "lucide-react";
import { cn } from "@/lib/utils";
import { useApproveAndMerge, useMarkMergeReady } from "../hooks/use-actions";
import { actorName } from "../lib/people";
import { useIsAdmin } from "../lib/permissions";
import type { Tone } from "../lib/status";
import { asSentence, mergeAction, needsApproval } from "../lib/tasks";
import type { MergeQueueItem } from "../types";
import { StatusBadge, ToneBadge } from "./status-badge";
import { Button } from "./ui/button";

const checkTone = (value: string): Tone =>
  value === "success" || value === "approved"
    ? "emerald"
    : value === "failure" || value === "changes_requested"
      ? "red"
      : "neutral";

/**
 * Queue actions for one pull request: mark a draft ready, or approve, comment, and squash-merge.
 * Admins only; everyone else approves through business sign-off.
 */
export function MergeActions({
  item,
  layout = "inline",
  onDone,
}: {
  item: MergeQueueItem;
  layout?: "inline" | "stacked";
  onDone?: () => void;
}) {
  const admin = useIsAdmin();
  const action = mergeAction(item);
  const markReady = useMarkMergeReady();
  const ship = useApproveAndMerge();
  const pending = markReady.isPending || ship.isPending;
  const wide = layout === "stacked";
  const buttonSize = wide ? "default" : "sm";
  if (!admin) return null;
  if (action === "ready") {
    return (
      <Button
        className={wide ? "w-full" : undefined}
        size={buttonSize}
        disabled={pending}
        onClick={() => markReady.mutate({ itemId: item.id, taskId: item.taskId }, { onSuccess: onDone })}
      >
        Mark ready for review
      </Button>
    );
  }
  if (action === "ship") {
    return (
      <Button
        className={wide ? "w-full" : undefined}
        size={buttonSize}
        disabled={pending}
        onClick={() => ship.mutate({ itemId: item.id, taskId: item.taskId }, { onSuccess: onDone })}
      >
        <Check /> Approve and merge
      </Button>
    );
  }
  return null;
}

/** A merge-queue ticket: PR, checks, blockers, and the action for its current status. */
export function MergeTicket({
  item,
  className,
  onDone,
}: {
  item: MergeQueueItem;
  className?: string;
  onDone?: () => void;
}) {
  const waiting = needsApproval(item);
  const action = mergeAction(item);
  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border p-4 text-sm",
        waiting && "border-amber-500/40 bg-amber-500/5",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <a
            href={item.prUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-medium hover:underline"
          >
            <GitPullRequest className="size-4 text-muted-foreground" />
            PR #{item.prNumber}
            <ExternalLink className="size-3 text-muted-foreground" />
          </a>
          <p className="truncate font-mono text-xs text-muted-foreground">
            {item.headBranch} → {item.baseBranch}
          </p>
        </div>
        <StatusBadge status={item.status} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        <ToneBadge tone={checkTone(item.checks.ci)}>CI {item.checks.ci}</ToneBadge>
        <ToneBadge tone={checkTone(item.checks.review)}>
          Review {item.checks.review.replace("_", " ")}
        </ToneBadge>
        {item.checks.conflicts && <ToneBadge tone="red">Conflicts</ToneBadge>}
        {action === "ready" && <ToneBadge tone="amber">Draft</ToneBadge>}
        {action === "ship" && <ToneBadge tone="sky">Ready for review</ToneBadge>}
        {item.lane === "hotfix" && <ToneBadge tone="amber">Hotfix</ToneBadge>}
        {item.releaseTag && (
          <ToneBadge tone="violet">
            <Tag /> {item.releaseTag}
          </ToneBadge>
        )}
        <ToneBadge tone="neutral">Position {item.position}</ToneBadge>
      </div>
      {item.blockers.length > 0 && (
        <ul className="space-y-1 text-amber-700 dark:text-amber-300">
          {item.blockers.map((b) => (
            <li key={b} className="flex gap-2">
              <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
              {asSentence(b)}
            </li>
          ))}
        </ul>
      )}
      {item.approvedBy && (
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <Check className="size-3.5" /> Approved by {actorName(item.approvedBy) ?? "Someone"}
        </p>
      )}
      {item.mergedSha && (
        <p className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300">
          <GitMerge className="size-3.5" /> Merged{" "}
          <span className="font-mono">{item.mergedSha.slice(0, 7)}</span>
        </p>
      )}
      {item.error && <p className="text-destructive">{item.error}</p>}
      <MergeActions item={item} layout="stacked" onDone={onDone} />
    </div>
  );
}
